/* ERS theme — product page: gallery, options, sticky add to cart, ask-a-question modal, recently viewed. */
(() => {
  'use strict';
  if (window.ERSProductLoaded) return;
  window.ERSProductLoaded = true;

  // Elements can be moved in the theme editor, which runs connectedCallback again.
  const firstConnect = (el) => {
    if (el.ersConnected) return false;
    el.ersConnected = true;
    return true;
  };

  /* ---------- Gallery ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      if (!firstConnect(this)) return;
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.counter = this.querySelector('[data-gallery-current]');
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      if (this.index > 0) requestAnimationFrame(() => this.goTo(this.index, false));

      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.galleryThumb))));
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.goTo(this.index + 1));

      let timer;
      this.track.addEventListener(
        'scroll',
        () => {
          clearTimeout(timer);
          timer = setTimeout(() => {
            const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
            if (i !== this.index) this.setActive(i);
          }, 60);
        },
        { passive: true }
      );

      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.goTo(this.index + 1);
        if (event.key === 'ArrowLeft') this.goTo(this.index - 1);
      });

      this.lightbox = this.querySelector('modal-dialog.lightbox');
      this.querySelectorAll('[data-gallery-open]').forEach((button) =>
        button.addEventListener('click', () => this.openLightbox(Number(button.dataset.galleryOpen), button))
      );
    }

    goTo(i, smooth = true) {
      if (!this.slides.length) return;
      const index = (i + this.slides.length) % this.slides.length;
      this.track.scrollTo({ left: this.track.clientWidth * index, behavior: smooth ? 'smooth' : 'auto' });
      this.setActive(index);
    }

    goToMedia(mediaId) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i > -1) this.goTo(i);
    }

    setActive(index) {
      this.index = index;
      this.slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
      this.thumbs.forEach((t, i) => {
        t.classList.toggle('is-active', i === index);
        if (i === index) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = index + 1;
      const active = this.thumbs[index];
      if (active) active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    openLightbox(index, trigger) {
      if (!this.lightbox) return;
      this.lightbox.open(trigger);
      const item = this.lightbox.querySelector(`[data-lightbox-index="${index}"]`);
      requestAnimationFrame(() => item?.scrollIntoView({ block: 'start' }));
    }
  }
  if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      if (!firstConnect(this)) return;
      this.addEventListener('change', () => {
        const selected = Array.from(this.querySelectorAll('fieldset')).map((fieldset) => {
          const checked = fieldset.querySelector('input:checked');
          const label = fieldset.querySelector('[data-selected-value]');
          if (checked && label) label.textContent = checked.value;
          return checked ? checked.value : null;
        });
        this.dispatchEvent(new CustomEvent('variant:select', { bubbles: true, detail: { options: selected } }));
      });
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ---------- Product page ---------- */
  class ProductPage extends HTMLElement {
    connectedCallback() {
      try {
        this.data = JSON.parse(this.querySelector('[data-product-json]').textContent);
      } catch (e) {
        this.data = { variants: [] };
      }
      this.sectionId = this.dataset.sectionId;
      this.initStickyBar();
      if (!firstConnect(this)) return;
      this.addEventListener('variant:select', (event) => this.onVariantSelect(event.detail.options));
      this.initAskModal();
      this.rememberProduct();
      // Links such as /products/handle#ask-availability open the question form in that mode.
      const askMatch = window.location.hash.match(/^#ask-(question|volume|availability)$/);
      if (askMatch) {
        const trigger = this.querySelector(`[data-ask-mode="${askMatch[1]}"]`);
        if (trigger) requestAnimationFrame(() => trigger.click());
      }
    }

    onVariantSelect(options) {
      const variant = this.data.variants.find((v) => v.options.every((value, i) => value === options[i]));
      const idInput = this.querySelector('[data-variant-id-input]');
      const addButtons = this.querySelectorAll('[data-add-button], [data-sticky-add]');
      if (!variant) {
        addButtons.forEach((b) => {
          b.disabled = true;
          const label = b.querySelector('[data-add-label]');
          if (label) label.textContent = (window.theme.strings || {}).unavailable;
        });
        return;
      }
      if (idInput) idInput.value = variant.id;
      const url = `${this.dataset.productUrl}?variant=${variant.id}`;
      window.history.replaceState({}, '', url);
      if (variant.featured_media) this.querySelector('media-gallery')?.goToMedia(variant.featured_media);
      this.refresh(url);
    }

    async refresh(url) {
      // Only the latest selection may update the page; older requests are cancelled.
      this.refreshController?.abort();
      const controller = new AbortController();
      this.refreshController = controller;
      this.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${url}&section_id=${encodeURIComponent(this.sectionId)}`, { signal: controller.signal });
        const html = await response.text();
        if (controller !== this.refreshController) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const swap = (selector) => {
          const fresh = doc.querySelector(selector);
          const current = this.querySelector(selector);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        };
        ['[data-variant-price]', '[data-variant-stock]', '[data-variant-ids]', '[data-variant-specs]', '[data-sticky-price]'].forEach(swap);

        const syncButton = (selector) => {
          const fresh = doc.querySelector(selector);
          const current = this.querySelector(selector);
          if (!fresh || !current) return;
          current.disabled = fresh.disabled;
          current.innerHTML = fresh.innerHTML;
        };
        syncButton('[data-add-button]');
        syncButton('[data-sticky-add]');

        const toggleHidden = (selector) => {
          const fresh = doc.querySelector(selector);
          const current = this.querySelector(selector);
          if (fresh && current) current.classList.toggle('is-hidden', fresh.classList.contains('is-hidden'));
        };
        toggleHidden('[data-dynamic-checkout]');
        toggleHidden('[data-soldout-help]');
        ['[data-buy-box]', '[data-sticky-buy]'].forEach((selector) => {
          const fresh = doc.querySelector(selector);
          const current = this.querySelector(selector);
          if (fresh && current) current.classList.toggle('is-sold-out', fresh.classList.contains('is-sold-out'));
        });

        const freshCutoff = doc.querySelector('[data-ship-cutoff]');
        const cutoff = this.querySelector('[data-ship-cutoff]');
        if (freshCutoff && cutoff) {
          cutoff.toggleAttribute('data-sold-out', freshCutoff.hasAttribute('data-sold-out'));
          cutoff.update?.();
        }

        const freshQty = doc.querySelector('[data-quantity-input]');
        const qty = this.querySelector('[data-quantity-input]');
        if (freshQty && qty) {
          if (freshQty.hasAttribute('max')) qty.max = freshQty.max;
          else qty.removeAttribute('max');
          if (qty.max && Number(qty.value) > Number(qty.max)) qty.value = qty.max;
          qty.dispatchEvent(new Event('change'));
        }
      } catch (e) {
        if (e.name !== 'AbortError') window.location.href = url;
      } finally {
        if (controller === this.refreshController) this.removeAttribute('aria-busy');
      }
    }

    // The sticky bar shows whenever the page's own buy button is off screen (above or below it, so
    // phones see Add to cart straight away), and hides over the footer. Sold out, the page's
    // "Ask about availability" button stands in for Add to cart, in the page and in the bar.
    // Observers report all of this without measuring the page on every scroll.
    initStickyBar() {
      this.stickyBar = this.querySelector('[data-sticky-buy]');
      const anchors = [this.querySelector('[data-add-button]'), this.querySelector('[data-soldout-help]')].filter(Boolean);
      this.stickyObserver?.disconnect();
      this.footerObserver?.disconnect();
      if (!this.stickyBar || !anchors.length || !('IntersectionObserver' in window)) return;
      this.stickyBar.hidden = false;
      const footer = document.querySelector('.footer');
      const inView = new Set();
      let measured = false;
      let footerInView = false;
      const apply = () => {
        const show = measured && inView.size === 0 && !footerInView;
        if (show === this.stickyBar.classList.contains('is-visible')) return;
        this.stickyBar.classList.toggle('is-visible', show);
        document.body.classList.toggle('has-sticky-buy', show);
      };
      // A button hidden with display: none never counts as on screen, so whichever of the two is
      // showing decides. The part of the screen under the sticky header doesn't count either.
      const header = document.querySelector('sticky-header');
      const headerWrapper = header?.closest('.header-wrapper');
      const headerHeight = header && headerWrapper && getComputedStyle(headerWrapper).position === 'sticky' ? Math.round(header.offsetHeight) : 0;
      this.stickyObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => (entry.isIntersecting ? inView.add(entry.target) : inView.delete(entry.target)));
          measured = true;
          apply();
        },
        { rootMargin: `-${headerHeight}px 0px 0px 0px` }
      );
      anchors.forEach((anchor) => this.stickyObserver.observe(anchor));
      if (footer) {
        this.footerObserver = new IntersectionObserver(([entry]) => {
          footerInView = entry.isIntersecting;
          apply();
        });
        this.footerObserver.observe(footer);
      }
    }

    disconnectedCallback() {
      this.stickyObserver?.disconnect();
      this.footerObserver?.disconnect();
      this.refreshController?.abort();
      document.body.classList.remove('has-sticky-buy');
      this.stickyBar?.classList.remove('is-visible');
    }

    initAskModal() {
      const modal = this.querySelector('.ask-modal');
      if (!modal) return;
      modal.addEventListener('modal:open', (event) => {
        const trigger = event.detail.trigger;
        const mode = (trigger && trigger.dataset && trigger.dataset.askMode) || 'question';
        const title = modal.querySelector('[data-ask-title]');
        const message = modal.querySelector('[data-ask-message]');
        const quantity = modal.querySelector('[data-ask-quantity]');
        const modeInput = modal.querySelector('[data-ask-mode-input]');
        const titles = { question: modal.dataset.titleQuestion, volume: modal.dataset.titleVolume, availability: modal.dataset.titleAvailability };
        const messages = { question: modal.dataset.messageQuestion, volume: modal.dataset.messageVolume, availability: modal.dataset.messageAvailability };
        if (title && titles[mode]) title.textContent = titles[mode];
        const submit = modal.querySelector('[data-ask-submit]');
        const sendLabels = { question: modal.dataset.sendQuestion, volume: modal.dataset.sendVolume, availability: modal.dataset.sendAvailability };
        if (submit && sendLabels[mode]) submit.textContent = sendLabels[mode];
        if (modeInput) modeInput.value = mode;
        if (quantity) quantity.hidden = mode !== 'volume';
        if (message) {
          const untouched = !message.value.trim() || Object.values(messages).includes(message.value);
          if (untouched) message.value = messages[mode] || message.value;
        }
      });
    }

    rememberProduct() {
      try {
        const key = 'ers-recently-viewed';
        const handle = this.dataset.productHandle;
        const list = JSON.parse(localStorage.getItem(key) || '[]').filter((h) => h !== handle);
        list.unshift(handle);
        localStorage.setItem(key, JSON.stringify(list.slice(0, 12)));
      } catch (e) {}
    }
  }
  if (!customElements.get('product-page')) customElements.define('product-page', ProductPage);

  /* ---------- Ship-today countdown ---------- */
  // "Order within 2 h 14 min and it ships today" before the cutoff on shipping days (Monday to Friday,
  // minus closed dates), otherwise "Order now and it ships Monday". Times are in the shop's time zone.
  class ShipCutoff extends HTMLElement {
    connectedCallback() {
      this.text = this.querySelector('[data-cutoff-text]');
      if (!this.text) return;
      this.update();
      clearInterval(this.timer);
      this.timer = setInterval(() => this.update(), 30000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    // Today's date and time on the shop's clock.
    now() {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: this.dataset.timezone || 'America/Los_Angeles',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hourCycle: 'h23',
      }).formatToParts(new Date());
      const get = (type) => Number(parts.find((part) => part.type === type)?.value);
      return { date: Date.UTC(get('year'), get('month') - 1, get('day')), minutes: get('hour') * 60 + get('minute') };
    }

    update() {
      if (!this.text) return;
      if (this.hasAttribute('data-sold-out')) {
        this.hidden = true;
        return;
      }
      let now;
      try {
        now = this.now();
      } catch (e) {
        this.hidden = true;
        return;
      }
      const closed = new Set((this.dataset.closed || '').split(/[\s,]+/).filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d)));
      // Dates are handled as UTC midnights so adding days never trips over daylight saving.
      const isShipDay = (utcMidnight) => {
        const day = new Date(utcMidnight);
        const weekday = day.getUTCDay();
        return weekday !== 0 && weekday !== 6 && !closed.has(day.toISOString().slice(0, 10));
      };
      const cutoff = (Number(this.dataset.cutoffHour) || 14) * 60;
      let message = '';
      if (isShipDay(now.date) && now.minutes < cutoff) {
        const left = cutoff - now.minutes;
        const hours = Math.floor(left / 60);
        const minutes = left % 60;
        const time = hours > 0
          ? (this.dataset.labelHours || '[hours] h [minutes] min').replace('[hours]', hours).replace('[minutes]', minutes)
          : (this.dataset.labelMinutes || '[minutes] min').replace('[minutes]', Math.max(1, minutes));
        message = (this.dataset.textToday || '').replace('[time]', time);
      } else {
        for (let i = 1; i <= 21; i += 1) {
          const day = now.date + i * 86400000;
          if (!isShipDay(day)) continue;
          const name = i === 1
            ? this.dataset.labelTomorrow || 'tomorrow'
            : new Intl.DateTimeFormat(document.documentElement.lang || 'en', { weekday: 'long', timeZone: 'UTC' }).format(new Date(day));
          message = (this.dataset.textNext || '').replace('[day]', name);
          break;
        }
      }
      this.text.textContent = message;
      this.hidden = !message;
    }
  }
  if (!customElements.get('ship-cutoff')) customElements.define('ship-cutoff', ShipCutoff);

  /* ---------- Review count link ---------- */
  // The count links to the reviews widget (#reviews by default). When that isn't on the page, look for
  // a widget from a common reviews app instead.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a.rating__count[href^="#"]');
    if (!link) return;
    const id = decodeURIComponent(link.getAttribute('href').slice(1));
    if (id && document.getElementById(id)) return;
    const widget = document.querySelector(
      '[data-fera-container], .fera-productReviews, #judgeme_product_reviews, .jdgm-review-widget, #shopify-product-reviews, #looxReviews, [data-oke-widget], #stamped-main-widget, .yotpo-main-widget'
    );
    if (!widget) return;
    event.preventDefault();
    if (!widget.hasAttribute('tabindex')) widget.setAttribute('tabindex', '-1');
    widget.focus({ preventScroll: true });
    widget.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  });

  /* ---------- Recommendations and recently viewed ---------- */
  class ProductRail extends HTMLElement {
    connectedCallback() {
      if (!firstConnect(this)) return;
      if (this.dataset.source === 'recent') this.loadRecent();
      else this.loadRecommendations();
    }

    async loadRecommendations() {
      const url = this.dataset.url;
      if (!url) return;
      try {
        const response = await fetch(url);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector(`product-rail[data-url]`);
        if (fresh && fresh.querySelector('li')) {
          this.innerHTML = fresh.innerHTML;
          this.closest('.product-rail-section')?.removeAttribute('hidden');
        }
      } catch (e) {}
    }

    async loadRecent() {
      let handles = [];
      try {
        handles = JSON.parse(localStorage.getItem('ers-recently-viewed') || '[]');
      } catch (e) {}
      const current = this.dataset.exclude;
      handles = handles.filter((h) => h !== current).slice(0, Number(this.dataset.limit || 8));
      if (!handles.length) return;
      // The cards come after the page has settled, so these requests never compete with it.
      await new Promise((resolve) => ('requestIdleCallback' in window ? requestIdleCallback(resolve, { timeout: 2500 }) : setTimeout(resolve, 300)));
      const list = this.querySelector('[data-rail-track]');
      const cards = await Promise.all(
        handles.map((handle) =>
          fetch(`${window.theme.routes.root}products/${encodeURIComponent(handle)}?section_id=product-card-api`)
            .then((r) => (r.ok ? r.text() : ''))
            .then((html) => {
              const doc = new DOMParser().parseFromString(html, 'text/html');
              return doc.querySelector('[data-product-card]')?.outerHTML || '';
            })
            .catch(() => '')
        )
      );
      const html = cards.filter(Boolean).map((c) => `<li>${c}</li>`).join('');
      if (!html) return;
      list.innerHTML = html;
      this.closest('.product-rail-section')?.removeAttribute('hidden');
      this.querySelector('scroll-rail')?.update?.();
    }
  }
  if (!customElements.get('product-rail')) customElements.define('product-rail', ProductRail);
})();
